import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { cloneDeep, forEach, get, isEmpty, isNil, keys, mapKeys, toInteger } from 'lodash';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { analytics } from '../../analytics';
import { aStyles, P } from '../typography';
import { Box, Flex } from '../grid';
import { Button } from '../button';
import { DefaultImage } from '../default';
import { Image } from '../image';
import { LoadingIndicator } from '../icons/loading-indicator';
import { Modal } from '../modal';
import { PeriPerksAlert } from './peri-perks-alert';
import { QuantityPicker } from '../quantity-picker';
import { StylePicker } from '../style-picker';

const PerksShopProductDetailModal = ({ onClose, handleSubmit, handleRemoveFromCart }) => {
  const { MemberStore, PerksShopStore } = useRootStore();
  const { loading, profile, isSignedIn } = MemberStore;
  const { selectedItem } = PerksShopStore;
  const product = get(selectedItem, 'product');
  const { image, pointsPrice, description, heading, hasStock, variants } = product;
  const [quantity, setQuantity] = useState(get(selectedItem, 'quantity') || 1);
  const hasSufficientFunds = isSignedIn && get(profile, 'balances.points') >= pointsPrice * quantity;
  const [productChoices, setProductChoices] = useState([]);
  const isProductUnavailable = !isSignedIn || !hasStock || !hasSufficientFunds;

  useEffect(() => {
    analytics.track(ANALYTICS_EVENTS.PRODUCT_VIEWED, {
      product_id: product.plu,
      name: product.name,
      category: product.category,
    });
  }, [product]);

  const validateChoices = () => productChoices.length === productChoices.filter(choice => choice.selected).length;

  const formatLabelBasedOnStock = (label, stock) => {
    if (stock === 0) {
      return `${label} (OUT OF STOCK)`;
    }
    return label;
  };

  const extractSelectedVariantData = () => {
    if (isEmpty(productChoices) && variants.length === 1) {
      return cloneDeep(variants[0]);
    }
    if (productChoices.length === 0) {
      return null;
    }
    let selectedVariant = cloneDeep(variants);
    productChoices.forEach(choice => {
      const selectedChoice = choice.variantTypes.find(({ id }) => id === choice.selected);
      if (selectedChoice) {
        selectedVariant = selectedVariant.filter(
          variant => variant[choice.title.toLowerCase()] === selectedChoice.label
        );
      }
    });

    if (!isEmpty(selectedVariant)) {
      return selectedVariant[0];
    }
    return null;
  };

  const updateQuantity = value => {
    const minVal = Math.max(value, 0);
    const selectedVariantData = extractSelectedVariantData();
    if (isEmpty(selectedVariantData) || isNil(selectedVariantData.stock)) {
      setQuantity(minVal);
    } else {
      setQuantity(Math.min(minVal, selectedVariantData.stock));
    }
  };

  const getMaxQty = () => {
    const selectedVariantData = extractSelectedVariantData();
    if (!validateChoices()) {
      return 1;
    }
    // calc max qty based on available points
    let maxQty = Math.floor(get(profile, 'balances.points') / pointsPrice);

    // check for stock
    if (selectedVariantData && !isNil(selectedVariantData.stock) && maxQty > selectedVariantData.stock) {
      maxQty = selectedVariantData.stock;
    }

    return maxQty;
  };

  const updateNextChoiceDataOnSelection = useCallback(
    (updatedChoices, selectedValue, primaryLabel, index) => {
      const selectedChoiceLabel = selectedValue.label;
      const sizeLabel = updatedChoices[index + 1].title.toLowerCase();
      const itemMap = {};
      variants
        .filter(variant => variant[primaryLabel] && variant[sizeLabel] && variant[primaryLabel] === selectedChoiceLabel)
        .forEach(variant => {
          itemMap[variant[sizeLabel]] = variant.stock;
        });
      return updatedChoices[index + 1].variantTypes.map(type => {
        if (keys(itemMap).includes(type.label)) {
          return {
            label: type.label,
            stock: itemMap[type.label],
            id: type.id,
          };
        }
        return {
          label: type.label,
          stock: 0,
          id: type.id,
        };
      });
    },
    [variants]
  );

  const onChoiceSelected = (selectedValue, index) => {
    const updatedOptions = cloneDeep(productChoices);
    updatedOptions[index].selected = toInteger(selectedValue.value);
    const primaryLabel = updatedOptions[index].title.toLowerCase();

    if (index + 1 === updatedOptions.length - 1) {
      updatedOptions[index + 1].selected = undefined;
      updatedOptions[index + 1].variantTypes = updateNextChoiceDataOnSelection(
        updatedOptions,
        selectedValue,
        primaryLabel,
        index
      );
      updatedOptions[index + 1].show = true;
    }
    setProductChoices(updatedOptions);
  };

  useEffect(() => {
    // Calculate max stock for color
    const calculateMaxStock = label => {
      let maxStock = 0;
      forEach(variants, variant => {
        if (variant.color === label) {
          if (variant.stock === null || maxStock === null) {
            maxStock = null;
          }
          if (variant.stock > maxStock) {
            maxStock = variant.stock;
          }
        }
      });
      return maxStock;
    };

    const updateChoicesForEdit = choices => {
      const selectedVariant = get(selectedItem, 'selectedVariant');
      const updatedChoices = cloneDeep(choices);
      forEach(choices, (choice, index) => {
        mapKeys(selectedVariant, (value, key) => {
          if (key !== 'plu' && key !== 'stock') {
            if (choice.title.toLowerCase() === key) {
              const selectedChoice = !isEmpty(choice.variantTypes)
                ? choice.variantTypes.find(({ label }) => label === value)
                : null;
              if (selectedChoice) {
                updatedChoices[index].selected = selectedChoice.id;
                updatedChoices[index].show = true;
                if (index + 1 === updatedChoices.length - 1) {
                  const primaryLabel = choice.title.toLowerCase();
                  updatedChoices[index + 1].variantTypes = updateNextChoiceDataOnSelection(
                    updatedChoices,
                    selectedChoice,
                    primaryLabel,
                    index
                  );
                }
              }
            }
          }
        });
      });
      return updatedChoices;
    };

    if (!isEmpty(variants)) {
      const choiceKeys = ['Color', 'Size'];
      // Base structure for choices for product
      let choices = [];
      forEach(choiceKeys, key => {
        choices.push({
          title: key,
          show: false,
          variantTypes: [],
          selected: undefined,
        });
      });
      const repeated = [];
      // calculate stock and id for choices for style picker
      forEach(variants, variant => {
        forEach(choiceKeys, (key, index) => {
          if (variant[key.toLowerCase()] && !repeated.includes(variant[key.toLowerCase()])) {
            choices[index].variantTypes.push({
              label: variant[key.toLowerCase()],
              stock: index === 0 ? calculateMaxStock(variant[key.toLowerCase()]) : variant.stock,
              id: choices[index].variantTypes.length + 1,
            });
            repeated.push(variant[key.toLowerCase()]);
          }
        });
      });
      choices = choices.filter(choice => choice.variantTypes.length > 0);
      if (choices.length > 0) {
        choices.some((choice, index) => {
          if (choice.variantTypes.length > 0) {
            choices[index].show = true;
            return true;
          }
          return false;
        });
        // update choices for edit
        if (get(selectedItem, 'selectedVariant')) {
          choices = updateChoicesForEdit(choices);
        }
      }
      setProductChoices(choices);
    }
  }, [variants, selectedItem, updateNextChoiceDataOnSelection]);

  const getDisabledButtonText = () => {
    if (!isSignedIn) {
      return 'Sign in or create an account';
    }
    if (!hasStock) {
      return 'Out of stock';
    }
    if (!hasSufficientFunds) {
      return 'You don’t have enough points';
    }
    return null;
  };

  const disabledButtonText = getDisabledButtonText();

  const modalButton = {
    primary: isProductUnavailable ? (
      <Button variant="action" disabled role="button" title={disabledButtonText}>
        {disabledButtonText}
      </Button>
    ) : (
      <Button
        onClick={() => {
          handleSubmit(selectedItem, extractSelectedVariantData(), quantity);
        }}
        variant="action"
        rightText={`${pointsPrice * quantity} POINTS`}
        disabled={!isSignedIn || !validateChoices()}
        role="button"
        title="Add to cart"
      >
        Add to cart
      </Button>
    ),
  };

  const modalScrollEnd = useCallback(
    node => {
      if (node !== null && get(selectedItem, 'selectedVariant')) {
        node.scrollIntoView({ block: 'end' });
      }
    },
    [selectedItem]
  );

  return (
    <Modal
      handleClose={onClose}
      modalTitle={heading}
      open
      buttons={modalButton}
      buttonTitle={heading}
      buttonPrice={`${pointsPrice} Points`}
      showShadow
    >
      <Box ref={modalScrollEnd}>
        {loading && <LoadingIndicator />}
        {!loading && (
          <>
            {!isEmpty(image) ? (
              <Image
                alt={image.alt}
                maxHeight="300px"
                objectFit="cover"
                src={image.url}
                srcSet={image.srcset}
                width="100%"
              />
            ) : (
              <DefaultImage height="300px" />
            )}
            <Box bg="background" px={2} py={1}>
              {description && (
                <Description variant={2} my={1}>
                  {description}
                </Description>
              )}
              {pointsPrice && (
                <Description variant={3} fontWeight="bold" my="0.5rem">
                  {pointsPrice} points
                </Description>
              )}
              {!isSignedIn && <PeriPerksAlert product={product} />}
            </Box>
            {!isEmpty(productChoices) && (
              <Box bg="background" mb={{ _: 2, lg: 0 }} height="fit-content">
                {productChoices.map((variant, index) => {
                  return (
                    variant.show && (
                      <StylePicker
                        key={variant.title}
                        title={variant.title}
                        type="radio"
                        required
                        items={variant.variantTypes.map(type => ({
                          label: formatLabelBasedOnStock(type.label, type.stock),
                          value: type.id.toString(),
                          disabled: type.stock !== null && type.stock < 1,
                        }))}
                        selectedItems={variant.selected === undefined ? [] : [variant.selected.toString()]}
                        onSelect={selectedValue => {
                          if (!selectedValue[0].disabled) {
                            onChoiceSelected(selectedValue[0], index);
                          }
                        }}
                      />
                    )
                  );
                })}
              </Box>
            )}
            <Flex bg="background" justifyContent="center" py={2}>
              <QuantityPicker
                quantity={quantity}
                increment={() => updateQuantity(quantity + 1)}
                decrement={() => updateQuantity(quantity - 1)}
                max={getMaxQty()}
                min={0}
              />
            </Flex>
            {handleRemoveFromCart && (
              <Flex my={2} justifyContent="center">
                <StyledButton
                  type="button"
                  variant={2}
                  textAlign="center"
                  onClick={() => handleRemoveFromCart(selectedItem)}
                >
                  Remove from order
                </StyledButton>
              </Flex>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

const StyledButton = styled.button`
  ${aStyles}
`;

const Description = styled(P)`
  white-space: pre-wrap;
`;

PerksShopProductDetailModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleRemoveFromCart: PropTypes.func,
};

PerksShopProductDetailModal.defaultProps = {
  handleRemoveFromCart: undefined,
};

const ObserverPerksShopForm = observer(PerksShopProductDetailModal);

export { ObserverPerksShopForm as PerksShopProductDetailModal };
